import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, appId } from '../../firebase/config';
import { useEditor } from '../../context/EditorContext';
import EditableElement from '../editor/EditableElement';
import { useNavigate } from 'react-router-dom';

const Footer = ({ setCurrentPage }) => {
    const { isEditMode } = useEditor();
    const [content, setContent] = useState({});
    const navigate = useNavigate();

    // Carregar dados
    useEffect(() => {
        const docName = isEditMode ? 'footer_draft' : 'footer';
        const unsub = onSnapshot(doc(db, `artifacts/${appId}/public/data/paginas`, docName), (docSnap) => {
            if (docSnap.exists()) setContent(docSnap.data());
        });
        return () => unsub();
    }, [isEditMode]);

    // Navegação SPA
    const handleLinkClick = (path) => {
        if (setCurrentPage) {
            const pageId = path.replace('/', '') || 'home';
            if (path === '/privacy') setCurrentPage('privacy_policy');
            else if (path === '/cookies') setCurrentPage('cookie_policy');
            else setCurrentPage(pageId);
        }
        navigate(path);
        window.scrollTo(0, 0);
    };

    return (
        <footer className="relative bg-gray-900 text-white pt-16 pb-8 mt-auto">
            
            {/* --- ZIG-ZAG SUAVE (IGUAL ÀS OUTRAS PÁGINAS) --- */}
            {/* Altura: 8px | Padrão: 20x8 | Cor: #111827 (bg-gray-900) */}
            <div 
                className="absolute left-0 w-full overflow-hidden leading-[0] z-20"
                style={{ 
                    height: '8px',
                    top: '-7px' // Posicionamento para "morder" a secção de cima
                }}
            >
                <svg width="100%" height="100%">
                    <defs>
                        <pattern id="sawtooth-footer" x="0" y="0" width="20" height="8" patternUnits="userSpaceOnUse">
                            <path d="M0,8 L10,0 L20,8" fill="#111827" stroke="none" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#sawtooth-footer)" />
                </svg>
            </div>

            <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 text-center md:text-left">
                    
                    {/* 1. MARCA & SOCIAL */}
                    <div>
                        <EditableElement section="paginas/footer" field="brandName" stylePrefix="footerBrand" label="Nome Marca">
                            <h3 className="text-2xl font-serif font-bold text-amber-500 mb-4 tracking-widest">
                                {content.brandName || "Vintage Praia"}
                            </h3>
                        </EditableElement>
                        <EditableElement section="paginas/footer" field="brandDesc" stylePrefix="footerText" type="longtext" label="Descrição">
                            <p className="text-gray-400 text-sm leading-relaxed font-light mb-6">
                                {content.brandDesc || "Sabores autênticos à beira-mar. Uma experiência onde a tradição encontra a sofisticação."}
                            </p>
                        </EditableElement>
                        
                        <div className="flex justify-center md:justify-start space-x-4">
                            <a href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-all"><i className="fab fa-facebook-f"></i></a>
                            <a href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-pink-600 hover:text-white transition-all"><i className="fab fa-instagram"></i></a>
                            <a href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-green-600 hover:text-white transition-all"><i className="fab fa-tripadvisor"></i></a>
                        </div>
                    </div>

                    {/* 2. CONTACTOS */}
                    <div>
                        <h4 className="text-lg font-bold uppercase tracking-widest mb-6 text-white border-b border-gray-800 pb-2 inline-block">Contactos</h4>
                        <ul className="space-y-4 text-gray-400 text-sm font-light">
                            {/* Morada */}
                            <li className="flex items-start justify-center md:justify-start">
                                <div className="w-6 flex-shrink-0 flex justify-center mt-1">
                                    <i className="fas fa-map-marker-alt text-amber-600"></i>
                                </div>
                                <EditableElement section="paginas/footer" field="address" stylePrefix="footerText" label="Morada">
                                    <span className="text-left">{content.address || "Av. Beira Mar 945, V. N. Gaia"}</span>
                                </EditableElement>
                            </li>
                            {/* Telefone */}
                            <li className="flex items-start justify-center md:justify-start">
                                <div className="w-6 flex-shrink-0 flex justify-center mt-1">
                                    <i className="fas fa-phone text-amber-600"></i>
                                </div>
                                <EditableElement section="paginas/footer" field="phone" stylePrefix="footerText" label="Telefone">
                                    <span>{content.phone || "224 060 831 | 911 048 081"}</span>
                                </EditableElement>
                            </li>
                            {/* Email */}
                            <li className="flex items-start justify-center md:justify-start">
                                <div className="w-6 flex-shrink-0 flex justify-center mt-1">
                                    <i className="fas fa-envelope text-amber-600"></i>
                                </div>
                                <EditableElement section="paginas/footer" field="email" stylePrefix="footerText" label="Email">
                                    <span>{content.email || "reservas@vintagepraia.pt"}</span>
                                </EditableElement>
                            </li>
                        </ul>
                    </div>

                    {/* 3. HORÁRIO */}
                    <div>
                        <h4 className="text-lg font-bold uppercase tracking-widest mb-6 text-white border-b border-gray-800 pb-2 inline-block">Horário</h4>
                        <ul className="space-y-3 text-gray-400 text-sm font-light">
                            <li className="flex flex-col md:flex-row md:justify-between gap-1 border-b border-gray-800 pb-2">
                                <span className="text-gray-500 text-center md:text-left">Seg - Qui</span>
                                <EditableElement section="paginas/footer" field="hoursWeek" stylePrefix="footerText" label="Horas Semana">
                                    <span className="text-white text-center md:text-right">{content.hoursWeek || "12:00 - 15:00 | 19:00 - 23:00"}</span>
                                </EditableElement>
                            </li>
                            <li className="flex flex-col md:flex-row md:justify-between gap-1 border-b border-gray-800 pb-2">
                                <span className="text-gray-500 text-center md:text-left">Sexta</span>
                                <EditableElement section="paginas/footer" field="hoursFriday" stylePrefix="footerText" label="Horas Sexta">
                                    <span className="text-white text-center md:text-right">{content.hoursFriday || "12:00 - 15:00 | 19:00 - 00:00"}</span>
                                </EditableElement>
                            </li>
                            <li className="flex flex-col md:flex-row md:justify-between gap-1 border-b border-gray-800 pb-2">
                                <span className="text-gray-500 text-center md:text-left">Sáb - Dom</span>
                                <EditableElement section="paginas/footer" field="hoursWeekend" stylePrefix="footerText" label="Horas Fim-de-Semana">
                                    <span className="text-white text-center md:text-right">{content.hoursWeekend || "12:00 - 00:00"}</span>
                                </EditableElement>
                            </li>
                        </ul>
                    </div>

                    {/* 4. LEGAL */}
                    <div>
                        <h4 className="text-lg font-bold uppercase tracking-widest mb-6 text-white border-b border-gray-800 pb-2 inline-block">Legal</h4>
                        <ul className="space-y-3 text-sm font-light">
                            <li>
                                <EditableElement section="paginas/footer" field="privacyText" stylePrefix="footerLegal" label="Texto Privacidade">
                                    <button onClick={() => handleLinkClick('/privacy')} className="text-gray-400 hover:text-amber-500 transition-colors uppercase tracking-wide text-xs">
                                        {content.privacyText || 'Política de Privacidade'}
                                    </button>
                                </EditableElement>
                            </li>
                            <li>
                                <EditableElement section="paginas/footer" field="cookiesText" stylePrefix="footerLegal" label="Texto Cookies">
                                    <button onClick={() => handleLinkClick('/cookies')} className="text-gray-400 hover:text-amber-500 transition-colors uppercase tracking-wide text-xs">
                                        {content.cookiesText || 'Política de Cookies'}
                                    </button>
                                </EditableElement>
                            </li>
                            <li>
                                <a href="https://www.livroreclamacoes.pt" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-amber-500 transition-colors uppercase tracking-wide text-xs flex items-center justify-center md:justify-start gap-2">
                                    <i className="fas fa-book-open text-xs"></i> Livro de Reclamações
                                </a>
                            </li>
                        </ul>
                    </div>

                </div>

                {/* COPYRIGHT */}
                <div className="border-t border-gray-800 mt-12 pt-8 text-center">
                    <EditableElement section="paginas/footer" field="copyright" stylePrefix="footerCopyright" label="Texto Copyright">
                        <p className="text-gray-600 text-[10px] uppercase tracking-widest">
                            &copy; {new Date().getFullYear()} {content.copyright || 'Vintage Praia. Todos os direitos reservados.'}
                        </p>
                    </EditableElement>
                </div>
            </div>
        </footer>
    );
};

export default Footer;